import { lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import { PopupAdDisplay } from "./components/news/PopupAdDisplay";

import { ScrollToTop } from "./components/ScrollToTop";
import { MobileInstallBanner } from "./components/MobileInstallBanner";

// ─── Lazy-loaded pages (route-level code splitting) ──────────────────
const Index = lazy(() => import("./pages/Index"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const LatestPage = lazy(() => import("./pages/LatestPage"));
const PopularPage = lazy(() => import("./pages/PopularPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const TagPage = lazy(() => import("./pages/TagPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const EditorialPolicyPage = lazy(() => import("./pages/EditorialPolicyPage"));
const CmsArticlesPage = lazy(() => import("./pages/CmsArticlesPage"));
const CmsArticleDetailPage = lazy(() => import("./pages/CmsArticleDetailPage"));
const BumSkokiPage = lazy(() => import("./pages/games/BumSkokiPage"));
const BumSkokiEmbedPage = lazy(() => import("./pages/games/BumSkokiEmbedPage"));
const LovNaPirhePage = lazy(() => import("./pages/games/LovNaPirhePage"));
const LovNaPirheEmbedPage = lazy(() => import("./pages/games/LovNaPirheEmbedPage"));
const BarvanjePirhaPage = lazy(() => import("./pages/games/BarvanjePirhaPage"));
const MemoryPirhiPage = lazy(() => import("./pages/games/MemoryPirhiPage"));
const ZombieShooterPage = lazy(() => import("./pages/games/ZombieShooterPage"));
const KekecAventuraPage = lazy(() => import("./pages/games/KekecAventuraPage"));
const PovodniMozPage = lazy(() => import("./pages/games/PovodniMozPage"));
const SlomythPage = lazy(() => import("./pages/games/SlomythPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages — rarely visited, always lazy
const AdminLayout = lazy(() => import("./layouts/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const BrandingPage = lazy(() => import("./pages/admin/BrandingPage"));
const NavigationPage = lazy(() => import("./pages/admin/NavigationPage"));
const FooterPage = lazy(() => import("./pages/admin/FooterPage"));
const CategoriesPage = lazy(() => import("./pages/admin/CategoriesPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const ArticlesPage = lazy(() => import("./pages/admin/ArticlesPage"));
const ArticleEditorPage = lazy(() => import("./pages/admin/ArticleEditorPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const AdsManagerPage = lazy(() => import("./pages/admin/AdsManagerPage"));
const ArticleAdGatePage = lazy(() => import("./pages/admin/ArticleAdGatePage"));
const CmsIntegrationPage = lazy(() => import("./pages/admin/CmsIntegrationPage"));
const NewsletterSettingsPage = lazy(() => import("./pages/admin/NewsletterSettingsPage"));
const SidebarSettingsPage = lazy(() => import("./pages/admin/SidebarSettingsPage"));
const LoginPage = lazy(() => import("./pages/admin/LoginPage"));
const SignupPage = lazy(() => import("./pages/admin/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/admin/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/admin/ResetPasswordPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PopupAdDisplay />
            
            <ScrollToTop />
            <MobileInstallBanner />
            <ChunkErrorBoundary>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/article/:slug" element={<ArticlePage />} />
                <Route path="/news/latest" element={<LatestPage />} />
                <Route path="/news/popular" element={<PopularPage />} />
                <Route path="/news/search" element={<SearchPage />} />
                <Route path="/category/zabava" element={<Navigate to="/category/spletne-igre-kvizi" replace />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/tag/:slug" element={<TagPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
                <Route path="/novice" element={<CmsArticlesPage />} />
                <Route path="/novice/:slug" element={<CmsArticleDetailPage />} />
                <Route path="/igre/bum-skoki" element={<BumSkokiPage />} />
                <Route path="/bum-skoki-game" element={<BumSkokiEmbedPage />} />
                <Route path="/igre/lov-na-pirhe" element={<LovNaPirhePage />} />
                <Route path="/lov-na-pirhe-game" element={<LovNaPirheEmbedPage />} />
                <Route path="/igre/barvanje-pirha" element={<BarvanjePirhaPage />} />
                <Route path="/igre/spomin-pirhi" element={<MemoryPirhiPage />} />
                <Route path="/igre/zombie-strelec" element={<ZombieShooterPage />} />
                <Route path="/igre/kekec-avantura" element={<KekecAventuraPage />} />
                <Route path="/igre/povodni-moz" element={<PovodniMozPage />} />
                <Route path="/igre/slomyth" element={<SlomythPage />} />
                
                {/* Admin Auth Routes */}
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/admin/signup" element={<SignupPage />} />
                <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
                
                {/* Protected Admin CMS Routes */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="articles" element={<ArticlesPage />} />
                  <Route path="articles/new" element={<ArticleEditorPage />} />
                  <Route path="articles/:id" element={<ArticleEditorPage />} />
                  <Route path="categories" element={<CategoriesPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="ads" element={<AdsManagerPage />} />
                  <Route path="ad-gate" element={<ArticleAdGatePage />} />
                  <Route path="branding" element={<BrandingPage />} />
                  <Route path="navigation" element={<NavigationPage />} />
                  <Route path="footer" element={<FooterPage />} />
                  <Route path="cms-integration" element={<CmsIntegrationPage />} />
                  <Route path="newsletter" element={<NewsletterSettingsPage />} />
                  <Route path="sidebar" element={<SidebarSettingsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ChunkErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
